import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const JPEG_QUALITY = 0.76;

const createCanvasSlice = (sourceCanvas, sourceY, sourceHeight) => {
  const safeSourceY = Math.max(0, Math.floor(sourceY));
  const safeSourceHeight = Math.max(
    1,
    Math.min(Math.floor(sourceHeight), sourceCanvas.height - safeSourceY)
  );

  const sliceCanvas = document.createElement('canvas');
  sliceCanvas.width = sourceCanvas.width;
  sliceCanvas.height = safeSourceHeight;

  const ctx = sliceCanvas.getContext('2d');
  ctx.drawImage(
    sourceCanvas,
    0,
    safeSourceY,
    sourceCanvas.width,
    safeSourceHeight,
    0,
    0,
    sliceCanvas.width,
    sliceCanvas.height
  );

  return sliceCanvas;
};

const addCanvasSegmentToPdf = ({
  pdf,
  canvas,
  sourceY,
  sourceHeight,
  layout,
  state,
}) => {
  const {
    margin,
    contentWidth,
    maxContentHeight,
    pageBottom,
    sectionGap,
  } = layout;

  const normalizedSourceY = Math.max(0, Math.floor(sourceY));
  const normalizedSourceHeight = Math.max(
    1,
    Math.min(Math.floor(sourceHeight), canvas.height - normalizedSourceY)
  );
  const segmentHeight = (normalizedSourceHeight / canvas.width) * contentWidth;

  if (segmentHeight <= maxContentHeight) {
    if (state.currentY > margin && state.currentY + segmentHeight > pageBottom) {
      pdf.addPage();
      state.currentY = margin;
    }

    const segmentCanvas = createCanvasSlice(canvas, normalizedSourceY, normalizedSourceHeight);
    const imgData = segmentCanvas.toDataURL('image/jpeg', JPEG_QUALITY);
    pdf.addImage(imgData, 'JPEG', margin, state.currentY, contentWidth, segmentHeight);
    state.currentY = Math.min(pageBottom, state.currentY + segmentHeight + sectionGap);
    return;
  }

  if (state.currentY > margin) {
    pdf.addPage();
    state.currentY = margin;
  }

  const maxSourceHeightPerPage = Math.floor((maxContentHeight / contentWidth) * canvas.width);
  let remainingSourceHeight = normalizedSourceHeight;
  let offsetY = 0;

  while (remainingSourceHeight > 0) {
    const sliceSourceHeight = Math.min(maxSourceHeightPerPage, remainingSourceHeight);
    const pageCanvas = createCanvasSlice(
      canvas,
      normalizedSourceY + offsetY,
      sliceSourceHeight
    );
    const pageImgData = pageCanvas.toDataURL('image/jpeg', JPEG_QUALITY);
    const pageImgHeight = (pageCanvas.height / canvas.width) * contentWidth;

    pdf.addImage(pageImgData, 'JPEG', margin, margin, contentWidth, pageImgHeight);

    remainingSourceHeight -= sliceSourceHeight;
    offsetY += sliceSourceHeight;

    if (remainingSourceHeight > 0) {
      pdf.addPage();
    } else {
      state.currentY = Math.min(pageBottom, margin + pageImgHeight + sectionGap);
    }
  }
};

export const exportToPDF = async (elementId, filename = 'academia-cortex.pdf') => {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error('Element not found:', elementId);
    return null;
  }

  try {
    // Add class for PDF capture mode
    document.body.classList.add('pdf-capture-mode');
    await new Promise((resolve) => requestAnimationFrame(resolve));

    // Use scale 1.5 instead of 2 for better balance of quality vs size
    const canvas = await html2canvas(element, {
      scale: 1.5,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false,
      allowTaint: true,
    });

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true, // Enable PDF compression
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pageWidth - margin * 2;
    const maxContentHeight = pageHeight - margin * 2;
    const pageBottom = pageHeight - margin;
    const sectionGap = 4;
    const layout = { margin, contentWidth, maxContentHeight, pageBottom, sectionGap };
    const state = { currentY: margin };
    const elementRect = element.getBoundingClientRect();
    const sectionElements = Array.from(element.querySelectorAll('[data-pdf-section]'));

    if (sectionElements.length > 0 && elementRect.height > 0) {
      const scaleY = canvas.height / elementRect.height;

      sectionElements.forEach((section) => {
        const sectionRect = section.getBoundingClientRect();
        const sectionTop = (sectionRect.top - elementRect.top) * scaleY;
        const sectionHeight = sectionRect.height * scaleY;

        if (sectionHeight > 0) {
          addCanvasSegmentToPdf({
            pdf,
            canvas,
            sourceY: sectionTop,
            sourceHeight: sectionHeight,
            layout,
            state,
          });
        }
      });
    } else {
      addCanvasSegmentToPdf({
        pdf,
        canvas,
        sourceY: 0,
        sourceHeight: canvas.height,
        layout,
        state,
      });
    }

    pdf.save(filename);

    // Remove PDF capture mode
    document.body.classList.remove('pdf-capture-mode');

    return true;
  } catch (error) {
    console.error('Error exporting PDF:', error);
    document.body.classList.remove('pdf-capture-mode');
    return null;
  }
};
